// noinspection JSUnusedGlobalSymbols

import {Ref} from '@vue/reactivity';
import {TinyEmitter} from 'tiny-emitter';
import {reactive} from 'vue';
import i18n from 'i18next';
import * as yup from 'yup';
import {Schema, ValidationError} from 'yup';
import {ProblemDetailsType, ValidationFailureExceptionType} from '@common/types';
import {HttpStatusCode} from 'axios';

export interface IFormErrorsTarget {
  [key: string]: string;
}

interface IFormSchema {
  [key: string]: yup.Schema<any, any, any, any>;
}

export interface IFormDefinition {
  [key: string]: {
    ref?: Ref,
    spec?: IFormDefinition | yup.Schema<any, any, any, any> | { (): yup.Schema<any, any, any, any> }
  };
}

export class FormValidator extends TinyEmitter {
  private readonly _definition: IFormDefinition;
  private readonly _validationErrors: IFormErrorsTarget;
  private readonly _model: Record<string, any>;

  // noinspection JSUnusedGlobalSymbols
  public get errors() {
    return this._validationErrors;
  }

  constructor(definition: IFormDefinition, model: Record<string, any>) {
    super();

    const validationErrorsTarget: IFormErrorsTarget = {};

    this._definition = definition;

    for (const fieldName in definition) {
      validationErrorsTarget[fieldName] = '';
    }

    this._validationErrors = reactive(validationErrorsTarget);
    this._model = model;
  }

  // noinspection JSUnusedGlobalSymbols
  public cleanErrors(): void {
    this.emit('onCleanErrors');

    for (const fieldName in this._validationErrors) {
      this._validationErrors[fieldName || ''] = '';
    }
  }

  // noinspection JSUnusedGlobalSymbols
  public isValid():  Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.validateForm()
        .then(() => {
          resolve(true);
        }).catch(() => {
          resolve(false);
      })
    });
  }

  public setProblemDetails(e: ProblemDetailsType): void {
    this.emit('onSetProblemDetails', e);
    
    if (e.status !== HttpStatusCode.BadRequest) {
      return;
    }

    const validationError = e as ValidationFailureExceptionType;
    if (!(validationError.Errors && Array.isArray(validationError.Errors))) {
      return;
    }

    const keys = Object.keys(this._validationErrors);
    const formSchema = this._buildFormSchema();

    validationError.Errors.forEach(value => {
      const propertyName = value.PropertyName ?? '';
      const errorMessage = value.ErrorMessage ?? '';
      const key = keys.find(k => k.toLowerCase() === propertyName.toLowerCase()) ?? '';
      const firstPart = `'${propertyName}'`;

      if (errorMessage.indexOf(firstPart) === 0) {
        const schema = formSchema.fields[key] as Schema;
        const label = schema?.spec?.label ?? propertyName;

        this._validationErrors[key] = `'${label}'${errorMessage.substring(firstPart.length)}`;
      } else {
        this._validationErrors[key] = i18n.t(errorMessage);
      }
    });
  }

  // noinspection JSUnusedGlobalSymbols
  public validateField(fieldName: string): void {
    const formSchema = this._buildFormSchema();

    formSchema
      .validateAt(fieldName, this._model)
      .then(() => {
        this._validationErrors[fieldName || ''] = '';
      })
      .catch(this._validationFailed);
  }

  public validateForm(): Promise<void> {
    const formSchema = this._buildFormSchema();

    this.emit('onCleanErrors');
    
    return new Promise<void>((resolve, reject) => {
      formSchema
        .validate(this._model, {abortEarly: false})
        .then(() => {
          resolve();
        })
        .catch((err) => {
          this._validationFailed(err);

          reject(err);
        });
    });
  }

  private _buildFormSchema = (): yup.AnyObjectSchema => {
    const fnBuild = (definition: IFormDefinition) : IFormSchema => {
      const localSchema: IFormSchema = {};
      
      for (const fieldName in definition) {
        const spec = definition[fieldName].spec;
        if (typeof spec === 'function') {
          localSchema[fieldName] = (<{(): yup.Schema<any, any, any, any>}>spec)();
          continue;
        }

        if (spec) {
          if (spec.spec) {
            localSchema[fieldName] = spec as yup.Schema<any, any, any, any>;
          } else {
            localSchema[fieldName] = yup.object(fnBuild(spec as IFormDefinition));
          }
        }
      }
      
      return localSchema;
    };

    const formSchema = fnBuild(this._definition);

    return yup.object(formSchema);
  };

  private _validationFailed = (err: ValidationError): void => {
    if (err.inner?.length) {
      err.inner.forEach(error => {
        this._validationErrors[error.path || ''] = error.message;
      });
    } else if (err.path) {
      this._validationErrors[err.path || ''] = err.message;
    } else {
      console.error(err);
    }
  };
}